import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Link2, Loader2, Clapperboard } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Clip() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/public/clip/${id}`);
        if (!r.ok) { if (alive) setError(r.status === 404 ? "This clip isn't available." : "Unable to load the clip."); return; }
        const j = await r.json();
        if (alive) { setData(j); setError(null); }
      } catch { if (alive) setError("Unable to load the clip."); }
    })();
    return () => { alive = false; };
  }, [id]);

  const copyLink = async () => {
    const url = `${window.location.origin}/api/public/clip/${id}/share`;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* noop */ }
  };

  const vertical = data?.aspect === "9:16";
  const square = data?.aspect === "1:1";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col items-center px-4 py-8" data-testid="public-clip-page">
      {/* subtle grain + glow */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #d946ef 0, transparent 40%), radial-gradient(circle at 80% 70%, #0ea5e9 0, transparent 40%)" }} />
      <header className="relative w-full max-w-3xl flex items-center gap-3 mb-6">
        {data?.channel?.logo_url ? (
          <img src={data.channel.logo_url} alt="" className="h-8 w-auto object-contain" />
        ) : (
          <Clapperboard className="h-6 w-6 text-fuchsia-400" />
        )}
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/60">{data?.channel?.name || "MOXYPLAY"}</span>
      </header>

      {error && <div className="relative mt-20 text-center text-white/70" data-testid="clip-error">{error}</div>}

      {!error && !data && (
        <div className="relative mt-24 flex items-center gap-2 text-white/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      )}

      {data && (
        <main className="relative w-full flex flex-col items-center">
          <div
            className={`rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black ${vertical ? "w-full max-w-[380px] aspect-[9/16]" : square ? "w-full max-w-[520px] aspect-square" : "w-full max-w-3xl aspect-video"}`}
            data-testid="clip-player-wrap"
          >
            <video
              src={data.video_url}
              poster={data.poster_url || undefined}
              controls
              playsInline
              autoPlay
              className="w-full h-full object-contain bg-black"
              data-testid="clip-video"
            />
          </div>

          <div className="w-full max-w-[520px] mt-5 text-center">
            <h1 className="font-display font-bold text-xl sm:text-2xl" data-testid="clip-title">{data.title}</h1>
            {data.caption && <p className="text-sm text-white/70 mt-2" data-testid="clip-caption">{data.caption}</p>}
            <div className="flex items-center justify-center gap-3 mt-5">
              <a href={data.video_url} download target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 transition-colors px-5 py-2.5 text-sm font-medium" data-testid="clip-download-btn">
                <Download className="w-4 h-4" /> Download
              </a>
              <button onClick={copyLink}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/40 transition-colors px-5 py-2.5 text-sm" data-testid="clip-copy-btn">
                <Link2 className="w-4 h-4" /> {copied ? "Copied!" : "Copy link"}
              </button>
            </div>
          </div>
        </main>
      )}

      <footer className="relative mt-auto pt-10 text-[11px] font-mono uppercase tracking-widest text-white/30">
        Powered by MOXYPLAY
      </footer>
    </div>
  );
}
